// The doctor's non-drug order sheet (Part B1). Phase 4 of
// docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§5.5/§6.8).
//
// A verbal/telephonic order is entered by the NURSE who takes the call —
// she IS the read-back, recorded at the same moment she creates the row.
// The ordering doctor countersigns it later, from their own session; a
// written order needs no countersign at all. Mirrors the role split the
// backend route enforces (routes/ip/clinical-orders.js): a nurse's "+ Order"
// form never offers 'written' mode.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import { FormPromptDialog } from '@/components/ui/TextPromptDialog';
import { ApiError } from '@/lib/api/client';
import VoiceFormFiller from '@/components/VoiceFormFiller';
import { appendText, matchByName, selectField, textField, vOneOf, vText, type Extracted } from '@/lib/voiceFill';
import { api } from '@/lib/api/client';
import { useAuth } from '@/hooks/useAuth';
import { fetchDoctors } from '@/features/appointments/api';
import {
  useClinicalOrders, useCreateClinicalOrder, useCountersignClinicalOrder,
  useDiscontinueClinicalOrder, useAmendClinicalOrder, useConsents,
} from '../hooks';
import type { ClinicalOrderType, ClinicalOrderMode, CodeStatusValue, ClinicalOrder } from '../types';
import { Section, Empty, useCan, fmtDateTime } from '../ui';

function useErr() {
  const [err, setErr] = useState<string | null>(null);
  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Something went wrong.');
  const node = err ? <div className="mb-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div> : null;
  return { fail, node, clear: () => setErr(null) };
}

const ORDER_TYPES: { value: ClinicalOrderType; label: string }[] = [
  { value: 'diet', label: 'Diet' },
  { value: 'activity', label: 'Activity' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'oxygen', label: 'Oxygen' },
  { value: 'isolation', label: 'Isolation' },
  { value: 'nursing', label: 'Nursing' },
  { value: 'code_status', label: 'Code status' },
  { value: 'blood_request', label: 'Blood request' },
  { value: 'other', label: 'Other' },
];
const MODES: ClinicalOrderMode[] = ['written', 'verbal', 'telephonic'];
const CODE_STATUS_VALUES: { value: CodeStatusValue; label: string }[] = [
  { value: 'full_code', label: 'Full code' },
  { value: 'dnar', label: 'DNAR' },
  { value: 'dni', label: 'DNI' },
  { value: 'comfort_care', label: 'Comfort care only' },
];

// A ward's commonest case: a nurse writing down a telephonic order — "Dr.
// Menon on phone: nil by mouth after midnight, monitor GRBS 6th hourly".
// The doctor is matched against the roster by name; read-back stays a pick
// (it is the person entering it, never something dictated on their behalf).
const ORDER_VOICE_FIELDS = [
  selectField('order_type', 'Order type — diet, activity, monitoring, oxygen, isolation, nursing, code_status, blood_request, other',
    ORDER_TYPES.map(t => t.value)),
  textField('text', 'The order itself, as instructed'),
  textField('frequency', 'Frequency or timing, e.g. q4h, 6th hourly, till further orders'),
  selectField('mode', 'How the order was given — written, verbal (in person) or telephonic (on phone)', MODES),
  textField('ordered_by', 'Name of the ordering doctor'),
  selectField('code_status_value', 'Code status — full_code, dnar, dni, comfort_care', CODE_STATUS_VALUES.map(c => c.value)),
];

const STATUS_BADGE: Record<ClinicalOrder['status'], 'success' | 'danger' | 'neutral' | 'warn'> = {
  active: 'success', completed: 'neutral', discontinued: 'danger', cancelled: 'neutral',
};

const EMPTY_FORM = {
  order_type: 'diet' as ClinicalOrderType,
  text: '', frequency: '',
  mode: 'written' as ClinicalOrderMode,
  ordered_by_user_id: '', read_back_by_user_id: '',
  code_status_value: '' as CodeStatusValue | '',
  linked_consent_id: '',
};

export function ClinicalOrdersSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const { user } = useAuth();
  const can = useCan();
  const mayWrite = can('ip:clinical-order') && !readOnly;
  const isNurse = user?.role === 'Nurse';
  const { data, isLoading } = useClinicalOrders(admissionId);
  const { data: consents } = useConsents(admissionId, mayWrite);
  const create = useCreateClinicalOrder(admissionId);
  const countersign = useCountersignClinicalOrder(admissionId);
  const discontinue = useDiscontinueClinicalOrder(admissionId);
  const amend = useAmendClinicalOrder(admissionId);
  const { fail, node, clear } = useErr();
  const [adding, setAdding] = useState(false);
  // A nurse's form never offers 'written' (see modeOptions below) — if the
  // default were the plain EMPTY_FORM's 'written', a nurse who never
  // touches the Mode field would submit a mode the native <select> never
  // actually showed her (the browser silently falls back to displaying the
  // first available option when the bound value isn't one of them, which
  // looks right on screen but leaves `f.mode` itself still 'written').
  // Seeding the right default up front keeps state and the rendered select
  // in agreement from the first paint.
  const [f, setF] = useState<typeof EMPTY_FORM>(() => ({ ...EMPTY_FORM, mode: isNurse ? 'verbal' : 'written' }));
  const [doctors, setDoctors] = useState<{ id: number; full_name: string }[]>([]);
  const [nurses, setNurses] = useState<{ id: number; full_name: string }[]>([]);
  const [discontinuing, setDiscontinuing] = useState<number | null>(null);
  const [discontinueReason, setDiscontinueReason] = useState('');
  // Amend asks for the reason and the corrected text in one dialog —
  // window.prompt() is silently refused by embedded browsers.
  const [amending, setAmending] = useState<ClinicalOrder | null>(null);

  useEffect(() => {
    fetchDoctors().then(setDoctors).catch(() => {});
    api.get<{ id: number; full_name: string }[]>('/users', { query: { role: 'Nurse' } }).then(setNurses).catch(() => {});
  }, []);

  const set = <K extends keyof typeof EMPTY_FORM>(k: K, v: (typeof EMPTY_FORM)[K]) => setF((p) => ({ ...p, [k]: v }));
  const reset = () => setF({ ...EMPTY_FORM, mode: isNurse ? 'verbal' : 'written' });
  const needsReadBack = f.mode !== 'written';
  const isDnar = f.order_type === 'code_status' && f.code_status_value === 'dnar';
  const dnrConsents = (consents ?? []).filter((c) => c.consent_type === 'dnr' && c.status === 'obtained' && !c.superseded);
  // Mirrors the server's NURSE_ALLOWED_MODES restriction — a nurse's own
  // "+ Order" form never even offers 'written', rather than letting her
  // pick it and then showing a 403.
  const modeOptions = isNurse ? MODES.filter((m) => m !== 'written') : MODES;

  const applyVoice = (x: Extracted) => {
    const type = vOneOf(x, 'order_type', ORDER_TYPES.map(t => t.value));
    const mode = vOneOf(x, 'mode', modeOptions);
    const code = vOneOf(x, 'code_status_value', CODE_STATUS_VALUES.map(c => c.value));
    const doctor = matchByName(vText(x, 'ordered_by'), doctors, d => d.full_name)?.id;
    setF(p => ({
      ...p,
      ...(type ? { order_type: type } : code ? { order_type: 'code_status' as const } : {}),
      ...(vText(x, 'text') ? { text: appendText(p.text, vText(x, 'text'), '; ') } : {}),
      ...(vText(x, 'frequency') ? { frequency: vText(x, 'frequency')! } : {}),
      ...(mode ? { mode } : {}),
      ...(code ? { code_status_value: code } : {}),
      ...(doctor ? { ordered_by_user_id: String(doctor) } : {}),
    }));
  };

  const submit = () => {
    clear();
    create.mutate({
      order_type: f.order_type,
      text: f.text,
      frequency: f.frequency || undefined,
      mode: f.mode,
      ordered_by_user_id: Number(f.ordered_by_user_id),
      read_back_by_user_id: needsReadBack ? Number(f.read_back_by_user_id) : undefined,
      code_status_value: f.order_type === 'code_status' && f.code_status_value ? f.code_status_value : undefined,
      linked_consent_id: isDnar && f.linked_consent_id ? Number(f.linked_consent_id) : undefined,
    }, {
      onSuccess: () => { setAdding(false); reset(); },
      onError: fail,
    });
  };

  const canSubmit = f.text.trim() && f.ordered_by_user_id
    && (!needsReadBack || f.read_back_by_user_id)
    && (!isDnar || f.linked_consent_id);

  return (
    <Section
      focusId="clinical_order"
      title="Doctor's order sheet"
      subtitle="Non-drug orders — diet, activity, monitoring, oxygen, isolation, nursing, code status, blood request."
      action={mayWrite && !adding && <Button size="sm" data-focus="clinical_order:new" onClick={() => setAdding(true)}>+ Order</Button>}
    >
      {adding && (
        <div className="mb-3 grid gap-2 rounded-lg border border-border bg-muted/40 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2 lg:col-span-4">
            <VoiceFormFiller fields={ORDER_VOICE_FIELDS} onFields={applyVoice} label="🎙 Dictate order"
              hint="Telephonic order from Dr. Menon: nil by mouth after midnight" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Order type</label>
            <NativeSelect value={f.order_type} onChange={(e) => set('order_type', e.target.value as ClinicalOrderType)}>
              {ORDER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </NativeSelect>
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-semibold">Order</label>
            <Input value={f.text} onChange={(e) => set('text', e.target.value)} placeholder="e.g. Diabetic 1800 kcal diet" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Frequency</label>
            <Input value={f.frequency} onChange={(e) => set('frequency', e.target.value)} placeholder="e.g. q4h" />
          </div>

          {f.order_type === 'code_status' && (
            <div>
              <label className="mb-1 block text-xs font-semibold">Code status</label>
              <NativeSelect value={f.code_status_value} onChange={(e) => set('code_status_value', e.target.value as CodeStatusValue)}>
                <option value="">— select —</option>
                {CODE_STATUS_VALUES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </NativeSelect>
            </div>
          )}
          {isDnar && (
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold">Linked DNR consent</label>
              <NativeSelect value={f.linked_consent_id} onChange={(e) => set('linked_consent_id', e.target.value)}>
                <option value="">— select a recorded DNR consent —</option>
                {dnrConsents.map((c) => <option key={c.id} value={c.id}>DNR consent · {fmtDateTime(c.obtained_at)} · {c.given_by}</option>)}
              </NativeSelect>
              {!dnrConsents.length && (
                <div className="mt-1 text-[11.5px] text-danger-ink">No obtained DNR consent is on file yet — record one in Consents first.</div>
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold">Mode</label>
            <NativeSelect value={f.mode} onChange={(e) => set('mode', e.target.value as ClinicalOrderMode)}>
              {modeOptions.map((m) => <option key={m} value={m}>{m}</option>)}
            </NativeSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Ordering doctor</label>
            <NativeSelect value={f.ordered_by_user_id} onChange={(e) => set('ordered_by_user_id', e.target.value)}>
              <option value="">— select —</option>
              {doctors.map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
            </NativeSelect>
          </div>
          {needsReadBack && (
            <div>
              <label className="mb-1 block text-xs font-semibold">Read back by (you)</label>
              <NativeSelect value={f.read_back_by_user_id} onChange={(e) => set('read_back_by_user_id', e.target.value)}>
                <option value="">— select —</option>
                {nurses.map((n) => <option key={n.id} value={n.id}>{n.full_name}</option>)}
              </NativeSelect>
            </div>
          )}

          <div className="flex justify-end gap-2 sm:col-span-2 lg:col-span-4">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); clear(); reset(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} disabled={!canSubmit} onClick={submit}>Record order</Button>
          </div>
        </div>
      )}
      {node}

      {isLoading ? <LoadingState label="Loading orders…" />
        : !data?.length ? <Empty label="No orders recorded" />
        : (
          <div className="space-y-2">
            {data.map((o) => {
              const finalized = o.mode === 'written' || !!o.countersigned_at;
              const canCountersign = mayWrite && o.status === 'active' && !finalized && o.read_back_by_user_id && user?.id === o.ordered_by_user_id;
              const canDiscontinue = mayWrite && !isNurse && o.status === 'active';
              const canAmend = mayWrite && !isNurse && finalized && o.status === 'active' && !o.superseded_by_order_id;
              return (
                <div key={o.id} data-focus={`clinical_order:${o.id}${!finalized && o.status === 'active' ? ' clinical_order:pending' : ''}`} className={`rounded-lg border border-border px-3 py-2 ${o.superseded_by_order_id ? 'opacity-60' : ''} ${!finalized ? 'bg-warn-soft/30' : ''}`}>
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13px] font-semibold">
                        {ORDER_TYPES.find((t) => t.value === o.order_type)?.label ?? o.order_type}
                      </span>
                      <span className="text-[13px]">{o.text}</span>
                      {o.frequency && <span className="text-[12px] text-ink-muted">· {o.frequency}</span>}
                      <Badge variant={STATUS_BADGE[o.status]}>{o.status}</Badge>
                      <Badge variant="neutral">{o.mode}</Badge>
                      {o.mode !== 'written' && !o.countersigned_at && <Badge variant="warn">Awaiting countersign</Badge>}
                      {o.superseded_by_order_id && <Badge variant="neutral">amended</Badge>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {canCountersign && (
                        <Button size="sm" variant="success" loading={countersign.isPending}
                          onClick={() => { clear(); countersign.mutate(o.id, { onError: fail }); }}>Countersign</Button>
                      )}
                      {canDiscontinue && (
                        discontinuing === o.id ? (
                          <div className="flex items-center gap-1.5">
                            <Input className="w-40" placeholder="Reason" value={discontinueReason} onChange={(e) => setDiscontinueReason(e.target.value)} />
                            <Button size="sm" loading={discontinue.isPending} disabled={!discontinueReason.trim()}
                              onClick={() => {
                                clear();
                                discontinue.mutate({ orderId: o.id, reason: discontinueReason }, {
                                  onSuccess: () => { setDiscontinuing(null); setDiscontinueReason(''); },
                                  onError: fail,
                                });
                              }}>Confirm</Button>
                            <Button variant="ghost" size="sm" onClick={() => { setDiscontinuing(null); setDiscontinueReason(''); }}>Cancel</Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => setDiscontinuing(o.id)}>Discontinue</Button>
                        )
                      )}
                      {canAmend && (
                        <Button variant="ghost" size="sm"
                          onClick={() => { amend.reset(); setAmending(o); }}>Amend</Button>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-ink-muted">
                    <span>By: Dr {o.ordered_by_name ?? o.ordered_by_user_id}</span>
                    {o.read_back_by_name && <span>Read back: {o.read_back_by_name}</span>}
                    {o.countersigned_by_name && <span>Countersigned: {o.countersigned_by_name} · {fmtDateTime(o.countersigned_at)}</span>}
                  </div>
                  {o.discontinue_reason && <div className="mt-1 text-[12.5px] text-ink-muted">Discontinued: {o.discontinue_reason}</div>}
                  {o.amendment_reason && <div className="mt-1 text-[12.5px] text-ink-muted">Amendment reason: {o.amendment_reason}</div>}
                  <div className="mt-1 text-[11px] text-ink-muted">
                    {fmtDateTime(o.start_datetime)}
                    {o.stop_datetime ? ` – ${fmtDateTime(o.stop_datetime)}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      <FormPromptDialog
        open={!!amending}
        title="Amend this order?"
        description="The original order is kept, marked amended, and the corrected text becomes a new version."
        fields={[
          { name: 'reason', label: 'Why is this order being amended?', multiline: true },
          { name: 'text', label: 'Corrected order text', initialValue: amending?.text ?? '', multiline: true },
        ]}
        confirmLabel="Amend order"
        pending={amend.isPending}
        error={amend.error instanceof Error ? amend.error.message : null}
        onConfirm={({ reason = '', text = '' }) => {
          if (!amending) return;
          clear();
          amend.mutate({ orderId: amending.id, amendment_reason: reason, changes: { text } }, { onSuccess: () => setAmending(null) });
        }}
        onClose={() => setAmending(null)}
      />
    </Section>
  );
}
